import { memo, useCallback, useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { XIcon } from 'lucide-react';
import { TranslationNS } from '@gokrew/models/constants';
import type { ReportingPerson } from '@gokrew/models/reporting';
import { ComboBox, type ComboBoxOption } from '@gokrew/ui';
import { Checkbox } from '@gokrew/ui/components/checkbox';
import { formatDate } from '@gokrew/ui/utility/utility';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import type {
  ManageTeamData,
  ManageTeamPlan,
  ManageTeamSelection,
} from '@/utils/manageTeam';
import { personName, temporaryGroupName } from '@/utils/reporting';
import { ManagerPicker } from '../ManagerPicker';
import styles from './ManagesTeamBlock.module.css';

interface ManagesTeamBlockProps {
  /** The person taking the team; null for a new hire not saved yet. */
  managerId: string | null;
  managerName: string;
  selection: ManageTeamSelection;
  onChange: (selection: ManageTeamSelection) => void;
  data: ManageTeamData;
  plan: ManageTeamPlan;
  isLoading?: boolean;
  /** `reporting.read`: the waiting teams and "Replace a manager" need it. */
  canRead: boolean;
  /** Show "Who will report to {name}?" (the dialog has it as its title). */
  showTitle?: boolean;
  /** Never offered in the people picker (e.g. the new hire's own manager). */
  excludeIds?: string[];
  disabled?: boolean;
}

/**
 * "Who will report to {name}?": the one block behind "Manages a team?" on
 * the Add employee form and "Assign direct reports" on an employee's page.
 * Three optional, combinable ways to add people: teams waiting for a manager
 * (temporary groups and people whose manager left), replacing a current
 * manager (their direct team, plus whether they now report to this person),
 * and one-off people. The live summary counts the people in the final plan.
 */
export const ManagesTeamBlock = memo(
  ({
    managerId,
    managerName,
    selection,
    onChange,
    data,
    plan,
    isLoading,
    canRead,
    showTitle = true,
    excludeIds,
    disabled,
  }: ManagesTeamBlockProps) => {
    const { t, i18n } = useTranslation(TranslationNS.Employee);
    const baseId = useId();

    const date = useCallback(
      (value: string) =>
        formatDate(value, {
          format: 'medium',
          locale: i18n.language?.slice(0, 2),
        }),
      [i18n.language]
    );

    const update = useCallback(
      (patch: Partial<ManageTeamSelection>) =>
        onChange({ ...selection, ...patch }),
      [onChange, selection]
    );

    const toggleKey = useCallback(
      (field: 'groupKeys' | 'orphanKeys', key: string, checked: boolean) => {
        const current = selection[field];
        update({
          [field]: checked
            ? [...current, key]
            : current.filter((value) => value !== key),
        });
      },
      [selection, update]
    );

    // Current managers: anyone with a direct report on a permanent line.
    const managers = useMemo(() => {
      const counts = new Map<string, number>();
      for (const node of data.orgChart) {
        if (!node.managerEmployeeId || node.isInterim) continue;
        counts.set(
          node.managerEmployeeId,
          (counts.get(node.managerEmployeeId) ?? 0) + 1
        );
      }
      return data.orgChart
        .filter(
          (node) => counts.has(node.employeeId) && node.employeeId !== managerId
        )
        .map((node) => ({ person: node, count: counts.get(node.employeeId)! }));
    }, [data.orgChart, managerId]);

    const managerOptions = useMemo<ComboBoxOption[]>(
      () =>
        managers.map(({ person, count }) => ({
          value: person.employeeId,
          label: personName(person),
          description: t('reporting_line.manages_team.direct_reports', {
            count,
          }),
        })),
      [managers, t]
    );

    const pickReplaced = useCallback(
      (id?: string) => {
        const found = managers.find((m) => m.person.employeeId === id);
        update({ replaced: found?.person ?? null, replacedJoins: true });
      },
      [managers, update]
    );

    const addPerson = useCallback(
      (person: ReportingPerson | null) => {
        if (
          !person ||
          selection.people.some((p) => p.employeeId === person.employeeId)
        )
          return;
        update({ people: [...selection.people, person] });
      },
      [selection.people, update]
    );

    const memberNames = (members: { employeeId: string; name: string }[]) =>
      members
        .filter((member) => member.employeeId !== managerId)
        .map((member) => member.name)
        .join(', ');

    const hasWaiting = data.groups.length > 0 || data.orphanTeams.length > 0;
    const replaced = selection.replaced;
    const replacedName = replaced ? personName(replaced) : '';
    const pickerExcludes = useMemo(
      () => [
        ...(excludeIds ?? []),
        ...selection.people.map((person) => person.employeeId),
      ],
      [excludeIds, selection.people]
    );

    return (
      <div className={styles.block}>
        {showTitle && (
          <h4 className={styles.title}>
            {t('reporting_line.manages_team.title', { name: managerName })}
          </h4>
        )}

        {isLoading && (
          <p className={styles.muted}>{t('reporting_line.loading')}</p>
        )}

        {hasWaiting && (
          <section className={styles.section}>
            <h5 className={styles.sectionTitle}>
              {t('reporting_line.manages_team.waiting_title')}
            </h5>
            <ul className={styles.teams}>
              {data.groups.map((group) => {
                const id = `${baseId}-g-${group.groupKey}`;
                return (
                  <li key={group.groupKey}>
                    <label className={styles.team} htmlFor={id}>
                      <Checkbox
                        id={id}
                        checked={selection.groupKeys.includes(group.groupKey)}
                        disabled={disabled}
                        onCheckedChange={(checked) =>
                          toggleKey('groupKeys', group.groupKey, !!checked)
                        }
                      />
                      <span className={styles.teamText}>
                        <span className={styles.teamName}>
                          {temporaryGroupName(t, group)}
                          <span className={styles.muted}>
                            {t('reporting_line.manages_team.group_meta', {
                              count: group.members.length,
                              cover: group.coveringManager.name,
                              date: date(group.since),
                            })}
                          </span>
                        </span>
                        <span className={styles.members}>
                          {memberNames(group.members)}
                        </span>
                      </span>
                    </label>
                  </li>
                );
              })}
              {data.orphanTeams.map((team) => {
                const id = `${baseId}-o-${team.key}`;
                return (
                  <li key={team.key}>
                    <label className={styles.team} htmlFor={id}>
                      <Checkbox
                        id={id}
                        checked={selection.orphanKeys.includes(team.key)}
                        disabled={disabled}
                        onCheckedChange={(checked) =>
                          toggleKey('orphanKeys', team.key, !!checked)
                        }
                      />
                      <span className={styles.teamText}>
                        <span className={styles.teamName}>
                          {team.formerManagerName
                            ? t('reporting_line.groups.former_reports', {
                                former: team.formerManagerName,
                              })
                            : t('reporting_line.manages_team.orphans')}
                          <span className={styles.muted}>
                            {t('reporting_line.manages_team.orphan_meta', {
                              count: team.members.length,
                            })}
                          </span>
                        </span>
                        <span className={styles.members}>
                          {memberNames(team.members)}
                        </span>
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        <div className={styles.columns}>
          {canRead && (
            <section className={styles.section}>
              <h5 className={styles.sectionTitle}>
                {t('reporting_line.manages_team.replace_title')}
              </h5>
              {replaced ? (
                <div className={styles.replaced}>
                  <div className={styles.replacedHeader}>
                    <span className={styles.teamName}>
                      {t('reporting_line.manages_team.replaced_team', {
                        manager: replacedName,
                        count: plan.replacedTeam.length,
                      })}
                    </span>
                    <button
                      type="button"
                      className={styles.remove}
                      disabled={disabled}
                      onClick={() => pickReplaced(undefined)}
                      aria-label={t('reporting_line.manages_team.remove', {
                        name: replacedName,
                      })}
                    >
                      <XIcon className={styles.removeIcon} aria-hidden />
                    </button>
                  </div>
                  {plan.replacedTeam.length > 0 && (
                    <span className={styles.members}>
                      {memberNames(plan.replacedTeam)}
                    </span>
                  )}
                  {plan.promotedFromTeam && (
                    <p className={styles.note}>
                      {plan.ownLine?.managerName
                        ? t('reporting_line.manages_team.promoted', {
                            name: managerName,
                            manager: replacedName,
                            above: plan.ownLine.managerName,
                          })
                        : t('reporting_line.manages_team.promoted_top', {
                            name: managerName,
                            manager: replacedName,
                          })}
                    </p>
                  )}
                  <RadioGroup
                    value={selection.replacedJoins ? 'joins' : 'stays'}
                    onValueChange={(value) =>
                      update({ replacedJoins: value === 'joins' })
                    }
                    disabled={disabled}
                    aria-label={replacedName}
                  >
                    <label className={styles.radio} htmlFor={`${baseId}-j`}>
                      <RadioGroupItem id={`${baseId}-j`} value="joins" />
                      {t('reporting_line.manages_team.replaced_joins', {
                        manager: replacedName,
                        name: managerName,
                      })}
                    </label>
                    <label className={styles.radio} htmlFor={`${baseId}-s`}>
                      <RadioGroupItem id={`${baseId}-s`} value="stays" />
                      {t('reporting_line.manages_team.replaced_stays', {
                        manager: replacedName,
                      })}
                    </label>
                  </RadioGroup>
                </div>
              ) : (
                <div className={styles.picker}>
                  <ComboBox
                    options={managerOptions}
                    value=""
                    onChange={pickReplaced}
                    disabled={disabled || isLoading}
                    placeholder={t('reporting_line.manages_team.pick_manager')}
                    searchPlaceholder={t('reporting_line.search_manager')}
                    noEntriesMessage={t(
                      'reporting_line.manages_team.no_managers'
                    )}
                    popoverClassName={styles.popover}
                  />
                </div>
              )}
            </section>
          )}

          <section className={styles.section}>
            <h5 className={styles.sectionTitle}>
              {t('reporting_line.manages_team.people_title')}
            </h5>
            <ManagerPicker
              employeeId={managerId ?? undefined}
              value={null}
              onChange={addPerson}
              allowNone={false}
              disabled={disabled}
              excludeIds={pickerExcludes}
              placeholder={t('reporting_line.manages_team.pick_person')}
            />
            {selection.people.length > 0 && (
              <ul className={styles.pills}>
                {selection.people.map((person) => (
                  <li key={person.employeeId} className={styles.pill}>
                    {personName(person)}
                    <button
                      type="button"
                      className={styles.remove}
                      disabled={disabled}
                      onClick={() =>
                        update({
                          people: selection.people.filter(
                            (p) => p.employeeId !== person.employeeId
                          ),
                        })
                      }
                      aria-label={t('reporting_line.manages_team.remove', {
                        name: personName(person),
                      })}
                    >
                      <XIcon className={styles.removeIcon} aria-hidden />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        {plan.conflicts.length > 0 && (
          <p className={styles.warning} role="alert">
            {t('reporting_line.manages_team.conflict', {
              names: plan.conflicts.map((c) => c.name).join(', '),
              name: managerName,
            })}
          </p>
        )}

        <p className={styles.summary} aria-live="polite">
          {plan.count
            ? t('reporting_line.manages_team.summary', {
                count: plan.count,
                name: managerName,
              })
            : t('reporting_line.manages_team.summary_none')}
        </p>
      </div>
    );
  }
);

ManagesTeamBlock.displayName = 'ManagesTeamBlock';
